/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {animated, AnimatedProps, useSpring} from 'react-spring';

export type AnimationProps = {
  fromLeft?: boolean;
  staggered?: boolean;
  index?: number;
};

type AnimatedWrapperProps = {
  children: React.ReactNode;
};

export type UseAnimatedWrapper = () => (props: AnimatedWrapperProps) => JSX.Element;

/**
 * @useAnimatedWrapper
 *
 * useAnimatedWrapper is a hook that returns an animated wrapper component
 * that can be used to animate any component.
 *
 * ```typescript
 *
 * import {useAnimatedWrapper} from '@ion/dls/src/hooks/';
 *
 * const SomeComponent = () => {
 * const AnimatedWrapper = useAnimatedWrapper();
 *
 *  return (
 *    <AnimatedWrapper>
 *      <SomeOtherComponent />
 *    </AnimatedWrapper>
 *  );
 * };
 *
 * ```
 *
 * @returns - animated wrapper component
 */
const useAnimatedWrapper = ({
  fromLeft = false,
  staggered = false,
  index = 1
}: AnimationProps) => {
  const delay = staggered ? index * 50 : 0;

  const animationY = useSpring<AnimatedProps<AnimatedWrapperProps>>({
    opacity   : 1,
    transform : 'translateY(0%)',
    from      : {opacity: 0, transform: 'translateY(-100%)'},
    config    : {
      duration : 300
    }
  });

  const animationX = useSpring<AnimatedProps<AnimatedWrapperProps>>({
    opacity   : 1,
    transform : 'translateX(0%)',
    from      : {opacity: 0, transform: 'translateX(-100%)'},
    config    : {
      duration : delay
    }
  });

  return (props: AnimatedWrapperProps) => (
    <animated.div style={fromLeft ? animationX : animationY}>
      {props.children}
    </animated.div>
  );
};

export default useAnimatedWrapper;
